import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useMediaPlayerContext, useMediaPlayerStateEvent } from '@verbit-ai/verbit-connect-web/player-provider'

import { WordTiming } from 'src/models/Transcript'
import { Selection } from 'src/utils/selection'
import { resolveRangesIn } from 'src/utils/highlightRange'
import { useCSSHighlight } from 'src/hooks/useCSSHighlight'
import { useActiveMediaTimingEvent, useActiveMediaTimingLookup } from 'src/hooks/useActiveMediaTiming'
import { useVariableRef } from 'src/hooks/useVariableRef'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useTranscriptHighLight } from 'src/contexts/TranscriptHighLightContext'
import { useTranscriptViewer } from 'src/contexts/TranscriptViewerProvider'
import { useTranscriptClient } from 'src/contexts/Transcript/TranscriptContext'
import {
    PLAYBACK_HIGHLIGHT,
    PLAYBACK_HIGHLIGHT_PRIORITY,
    TRANSCRIPT_HIGHLIGHT,
    TRANSCRIPT_HIGHLIGHT_PRIORITY,
} from 'src/components/TranscriptViewer/constants'

// Paragraph scroll alone is not enough when the cited words sit below the fold of a tall paragraph.
const scrollRangeIntoView = (container: HTMLElement, range: Range) => {
    const rangeRect = range.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    container.scrollTop = container.scrollTop - containerRect.top + rangeRect.top
}

const REVEAL_PADDING_PX = 8
// Long enough for the AI panel to open and the list to re-measure the re-wrapped paragraphs.
const REVEAL_SETTLE_MS = 1000
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const

// Scrolls only when the quote is not fully visible, then shows it with its paragraph's header (the time,
// speaker and inconsistency chips) when both fit, else with the quote at the top.
export const revealRangeIfHidden = (container: HTMLElement, range: Range) => {
    const containerRect = container.getBoundingClientRect()
    const rangeRect = range.getBoundingClientRect()
    if (rangeRect.top >= containerRect.top && rangeRect.bottom <= containerRect.bottom) {
        return
    }

    const paragraph = range.startContainer.parentElement?.closest('[data-index]')
    const paragraphTop = paragraph?.getBoundingClientRect().top ?? rangeRect.top
    const fitsWithHeader = rangeRect.bottom - paragraphTop <= containerRect.height - REVEAL_PADDING_PX * 2
    const top = fitsWithHeader ? paragraphTop : rangeRect.top
    container.scrollTop += top - containerRect.top - REVEAL_PADDING_PX
}

export const useTranscriptHighlight = () => {
    const { highlight } = useTranscriptHighLight()
    const { listRef, listScrollingContainerRef } = useTranscriptViewer()
    const transcriptClient = useTranscriptClient()

    const ranges = useMemo(() => highlight?.ranges ?? [], [highlight])
    const hasHighlight = ranges.length > 0

    const resolveRanges = useCallback(() => {
        const container = listScrollingContainerRef.current

        return container ? resolveRangesIn(container, ranges) : []
    }, [listScrollingContainerRef, ranges])

    const revealRef = useRef<{ reveal: () => void; stop: () => void } | null>(null)
    useEffect(() => () => revealRef.current?.stop(), [highlight?.id])

    // A click in the transcript also opens the AI panel: the narrower list re-wraps and re-measures over the
    // next frames, which can push the quote out of view after it painted. Each list layout change re-checks
    // it, until that settles or the user scrolls.
    const revealUntilSettled = (container: HTMLElement) => {
        revealRef.current?.stop()

        const revealNow = () => {
            const [range] = resolveRanges()
            if (range) {
                revealRangeIfHidden(container, range)

                return
            }

            // The re-layout pushed the quote's paragraph far enough to be unmounted: scroll the list to it, and
            // the layout change that mounts it places the quote.
            const index = transcriptClient.getParagraphIds().indexOf(ranges[0]?.paragraphId)
            if (index === -1) {
                stop()
            } else {
                listRef.current?.scrollToIndex(index, { align: 'start' })
            }
        }

        let frameId = 0
        const reveal = () => {
            cancelAnimationFrame(frameId)
            frameId = requestAnimationFrame(revealNow)
        }
        const stop = () => {
            cancelAnimationFrame(frameId)
            clearTimeout(timeoutId)
            USER_SCROLL_EVENTS.forEach((type) => container.removeEventListener(type, stop))
            if (revealRef.current?.stop === stop) {
                revealRef.current = null
            }
        }
        const timeoutId = setTimeout(stop, REVEAL_SETTLE_MS)
        USER_SCROLL_EVENTS.forEach((type) => container.addEventListener(type, stop, { passive: true }))

        revealRef.current = { reveal, stop }
        revealNow()
    }

    // Following playback owns the scroll (`useMediaPlaybackFollow`), as it does after a card click in the panel.
    useActiveMediaTimingEvent((timing) => {
        if (timing) {
            revealRef.current?.stop()
        }
    })

    // For the virtual list's `onChange`, which fires after it re-measures (and on scroll).
    const onListLayoutChange = useCallback(() => revealRef.current?.reveal(), [])

    useCSSHighlight({
        name: TRANSCRIPT_HIGHLIGHT,
        containerRef: listScrollingContainerRef,
        active: hasHighlight,
        priority: TRANSCRIPT_HIGHLIGHT_PRIORITY,
        highlightKey: highlight?.id,
        resolveRanges,
        onHighlightPainted: (domRanges) => {
            // Clear a stale native selection so it does not sit on top of the CSS highlight.
            Selection.clearSelection()

            const container = listScrollingContainerRef.current
            if (!container) {
                return
            }

            if (highlight?.reveal === 'ifHidden') {
                revealUntilSettled(container)
            } else {
                scrollRangeIntoView(container, domRanges[0])
            }
        },
    })

    return { onListLayoutChange }
}

/**
 * The playing word, as its own CSS highlight rather than a second use of `useTranscriptHighlight`: that one holds a
 * single range, so the quote would be replaced, and it scrolls and clears the selection on every paint.
 */
export const usePlaybackHighlight = () => {
    const isTextHighlightingEnabled = useFeatureFlag('text_highlighting')
    const { listScrollingContainerRef } = useTranscriptViewer()
    const { mediaPlayer } = useMediaPlayerContext()
    const findWordAt = useActiveMediaTimingLookup()
    const findWordAtRef = useVariableRef(findWordAt)
    const [isMediaPlaying, setIsMediaPlaying] = useState(false)
    const [word, setWord] = useState<WordTiming | null>(null)

    useMediaPlayerStateEvent((playerState) => setIsMediaPlaying(playerState.isMediaPlaying))

    // The player reports its time only every 300 ms, which skips short words, so read the audio clock every frame.
    useEffect(() => {
        if (!isTextHighlightingEnabled || !isMediaPlaying) {
            setWord(null)

            return
        }

        let frame = 0
        const readPlayingWord = () => {
            // Scheduled first, so one failed lookup can't stop the loop until the next play.
            frame = requestAnimationFrame(readPlayingWord)
            const timing = findWordAtRef.current(mediaPlayer.exactCurrentTime)
            setWord(timing)
        }
        readPlayingWord()

        return () => cancelAnimationFrame(frame)
    }, [isTextHighlightingEnabled, isMediaPlaying, mediaPlayer, findWordAtRef])

    const resolveRanges = useCallback(() => {
        const container = listScrollingContainerRef.current
        if (!container || !word) {
            return []
        }

        return resolveRangesIn(container, [{ paragraphId: word.paragraphId, start: word.charStart, end: word.charEnd }])
    }, [listScrollingContainerRef, word])

    useCSSHighlight({
        name: PLAYBACK_HIGHLIGHT,
        containerRef: listScrollingContainerRef,
        active: word !== null,
        priority: PLAYBACK_HIGHLIGHT_PRIORITY,
        highlightKey: word && `${word.paragraphId}:${word.charStart}`,
        resolveRanges,
    })
}
